/**
 * BackButton — the one true back-arrow used by every page that has
 * one (composer headers + detail-page covers + sub-pages). Clicking
 * goes back through the router's history; if the user landed here
 * via a deep link with no history to pop, we navigate to the
 * `fallbackTo` route instead so they never get stranded.
 *
 * Why this lives in `features/workspace`: it's a page-shell concern
 * (it knows about the router) and pairs with `WorkspaceShell` /
 * `Topbar`. `@flint/ui` stays router-agnostic.
 *
 * Visual contract: 32×32 icon button, neutral chrome, sky focus ring.
 * Matches the breadcrumb + topbar density so it reads as part of the
 * page chrome rather than a content-level affordance.
 */
import { useRouter } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { cn } from "@flint/ui";

export interface BackButtonProps {
  /**
   * Route to navigate to if there's nothing in router history to
   * pop — e.g. when the page was opened via a deep link. Stops the
   * user getting stuck or bouncing out of the app.
   */
  fallbackTo?: string;
  /** Accessible label. Default `"Back"`. Override for screen-reader clarity. */
  label?: string;
  className?: string;
}

export function BackButton({ fallbackTo, label = "Back", className }: BackButtonProps) {
  const router = useRouter();

  const handleClick = () => {
    // `window.history.length` is 1 on a fresh tab — no entries to pop.
    // Anything > 1 means there's at least one prior page in this tab's
    // history we can return to.
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.history.back();
      return;
    }
    if (fallbackTo) {
      // Caller may pass any internal route string; we sidestep TanStack's
      // strict route-tree generic since this is a runtime decision.
      void router.navigate({ to: fallbackTo as never });
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      className={cn(
        "inline-grid place-items-center w-9 h-9 rounded-md flex-shrink-0 cursor-pointer",
        "bg-paper border border-line text-ink-2",
        "hover:bg-bg hover:border-line-2 transition-colors",
        "focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2",
        className,
      )}
    >
      <ArrowLeft className="w-4 h-4" strokeWidth={2.2} aria-hidden />
    </button>
  );
}
